import React from 'react';
import { bool, func, number, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Field, Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

// Import configs and util modules
import appSettings from '../../../../config/settings';
import { intlShape, injectIntl, FormattedMessage } from '../../../../util/reactIntl';
import { ALLOWED, PRICE, propTypes } from '../../../../util/types';
import { isOldTotalMismatchStockError } from '../../../../util/errors';
import * as validators from '../../../../util/validators';
import { formatMoney } from '../../../../util/currency';
import { types as sdkTypes } from '../../../../util/sdkLoader';

// Import shared components
import { Button, Form, FieldCurrencyInput, FieldRadioButton, IconCollection } from '../../../../components';

// Import modules from this directory
import css from './EditListingPricingAndStockForm.module.css';
import { allowNegotiation as negotitation } from '../../../../config/configSearch';
import moment from 'moment';

const { Money } = sdkTypes;
const providerCommission = process.env.REACT_APP_PROVIDER_COMMISSION;

const getPriceValidators = (listingMinimumPriceSubUnits, marketplaceCurrency, intl) => {
  const priceRequiredMsgId = { id: 'EditListingPricingAndStockForm.priceRequired' };
  const priceRequiredMsg = intl.formatMessage(priceRequiredMsgId);
  const priceRequired = validators.required(priceRequiredMsg);

  const minPriceRaw = new Money(listingMinimumPriceSubUnits, marketplaceCurrency);
  const minPrice = formatMoney(intl, minPriceRaw);
  const priceTooLowMsgId = { id: 'EditListingPricingAndStockForm.priceTooLow' };
  const priceTooLowMsg = intl.formatMessage(priceTooLowMsgId, { minPrice });
  const minPriceRequired = validators.moneySubUnitAmountAtLeast(
    priceTooLowMsg,
    listingMinimumPriceSubUnits
  );

  return listingMinimumPriceSubUnits
    ? validators.composeValidators(priceRequired, minPriceRequired)
    : priceRequired;
};

export const EditListingPricingAndStockFormComponent = props => (
  <FinalForm
    {...props}
    render={formRenderProps => {
      const {
        formId,
        autoFocus,
        className,
        disabled,
        ready,
        handleSubmit,
        intl,
        invalid,
        pristine,
        marketplaceCurrency,
        unitType,
        listingMinimumPriceSubUnits,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        values,
        listing,
        initialValues,
        onSaveDraft,
        saveAsDraft,
        setSaveAsDraft,
        history
      } = formRenderProps;

      const { price, allowNegotiation, originalPrice, } = values;

      const priceValidators = getPriceValidators(
        listingMinimumPriceSubUnits,
        marketplaceCurrency,
        intl
      );

      const itemRequired = validators.required(
        intl.formatMessage({
          id: 'EditListingDetailsForm.itemRequired',
        })
      );

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress && !saveAsDraft;
      const submitDisabled = invalid || disabled || submitInProgress;
      const { updateListingError, showListingsError } = fetchErrors || {};

      const sellingFee = price ? new Money((((price?.amount / 100) * providerCommission) / 100) * 100, price.currency) : 0;
      const earingAmount = (price && sellingFee) ? new Money(price.amount - sellingFee.amount, price.currency) : 0;

      return (
        <Form onSubmit={handleSubmit} className={classes}>
          {updateListingError ? (
            <p className={css.error}>
              <FormattedMessage id="EditListingPricingAndStockForm.updateFailed" />
            </p>
          ) : null}
          {showListingsError ? (
            <p className={css.error}>
              <FormattedMessage id="EditListingPricingAndStockForm.showListingFailed" />
            </p>
          ) : null}
          <div className={css.selectField}>
            <div className={css.priceIconWrapper}>
              <sapn className={css.priceIcon}>$</sapn>
              <FieldCurrencyInput
                id={`${formId}originalPrice`}
                name="originalPrice"
                className={css.input}
                autoFocus={autoFocus}
                label={intl.formatMessage(
                  { id: 'EditListingPricingAndStockForm.pricePerProduct' },
                  { unitType }
                )}
                // placeholder={intl.formatMessage({
                //   id: 'EditListingPricingAndStockForm.priceInputPlaceholder',
                // })}
                currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
                validate={priceValidators}
              />
            </div>

            <div className={css.priceIconWrapper}>
              <sapn className={css.priceIcon}>$</sapn>
              <FieldCurrencyInput
                id={`${formId}price`}
                name="price"
                className={css.input}
                autoFocus={autoFocus}
                label={intl.formatMessage(
                  { id: 'EditListingPricingAndStockForm.askingPricePerProduct' },
                  { unitType }
                )}
                // placeholder={intl.formatMessage({
                //   id: 'EditListingPricingAndStockForm.priceInputPlaceholder',
                // })}
                currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
                validate={priceValidators}
              />
            </div>
          </div>
          <div className={css.priceDetailsWrapper}>

            <div>
              <label>
                <div className={css.priceLabel}>
                  <div className={css.customTooltip}>
                    <IconCollection icon="infoIcon" />
                    <div className={css.tooltipContent}>
                      <span> <IconCollection icon="infoIconWhite" /> </span>
                      <div>
                        <span><b><FormattedMessage id="EditListingPricingAndStockForm.negotiationsToolTipTitle" /></b></span><br /><br />
                        <span><FormattedMessage id="EditListingPricingAndStockForm.negotiationsToolTipContent" /></span>
                      </div>
                    </div>
                  </div>
                  <FormattedMessage id="EditListingTermsOfSaleForm.allowNegotiation" />
                </div>
              </label>
              <div className={css.radioBtn}>
                {negotitation.map(type => {
                  return (
                    <div>
                      <FieldRadioButton
                        key={type.key}
                        label={type.label}
                        value={type.key}
                        name="allowNegotiation"
                        id={type.key}
                        validate={itemRequired}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={css.priceDetails}>
              <div>
                <div>
                  <span className={css.priceLabel}>
                    <FormattedMessage id="EditListingPricingAndStockForm.askingPrice" />
                  </span>
                  <b>{price ? formatMoney(intl, price) : '00'}</b>
                </div>
                <div>
                  <div className={css.priceLabel}>
                    <div className={classNames(css.customTooltip, css.tooltipContent2)}>
                      <IconCollection icon="infoIcon" />
                      <div className={css.tooltipContent}>
                        <span> <IconCollection icon="infoIconWhite" /> </span>
                        <div>
                          <span><b><FormattedMessage id="EditListingPricingAndStockForm.sellingFeeToolTipTitle" /></b></span><br /><br />
                          <span>
                            <FormattedMessage id="EditListingPricingAndStockForm.sellingFeeToolTipContent" />
                          </span><br />
                          <span><br />
                            <FormattedMessage id="EditListingPricingAndStockForm.sellingFeeToolTipContent2" />
                          </span>
                        </div>
                      </div>
                    </div>
                    <FormattedMessage id="EditListingPricingAndStockForm.sellingFee" />
                  </div>
                  <b style={{ marginLeft: price ? '-11px' : '-3px' }}>
                    {price ? "-" : null}  {sellingFee ? formatMoney(intl, sellingFee) : '00'}
                  </b>
                </div>
                <div>
                  <span className={css.priceLabel}>
                    <FormattedMessage id="EditListingPricingAndStockForm.yourEaring" />
                  </span>
                  <b>{earingAmount ? formatMoney(intl, earingAmount) : '00'}</b>
                </div>
              </div>
            </div>
          </div>

          {invalid ? (
            <span className={css.error}>
              <FormattedMessage
                id="EditListingPricingAndStockForm.errorText"
                values={{ TabName: <b>{PRICE}</b> }}
              />
            </span>
          ) : null}
          <div className={css.buttonWrapper}>
            <Button
              className={css.submitButton}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
              ready={submitReady}
            >
              {saveActionMsg}
            </Button>

            <Button
              className={css.draftButton}
              type="button"
              disabled={submitDisabled}
              inProgress={saveAsDraft}
              onClick={() => {
                setSaveAsDraft(true);
                const { quantity } = listing?.currentStock?.attributes || {};
                const stock = quantity === 0 ? quantity : 1;
                const hasNoCurrentStock = quantity == null;
                const hasStockQuantityChanged = stock && stock !== initialValues.stock;
                const oldTotal = hasNoCurrentStock ? null : initialValues.stock;

                const stockUpdateMaybe =
                  hasNoCurrentStock || hasStockQuantityChanged
                    ? {
                      stockUpdate: {
                        oldTotal,
                        newTotal: stock,
                      },
                    }
                    : {};

                const updateValues = {
                  price: price,
                  publicData: {
                    originalPrice: originalPrice?.amount / 100,
                    askingPrice: price?.amount / 100,
                    updatedAt: Math.floor(Date.now() / 1000),
                    createdAt: moment(listing?.attributes?.createdAt).unix(),
                    allowNegotiation
                  },
                  ...stockUpdateMaybe,
                };
                onSaveDraft(updateValues).then(() => {
                  history.push('/listings')
                })
              }}
            >
              <FormattedMessage id="EditListingsDetailsForm.saveAsDraft" />
            </Button>
          </div>

        </Form>
      );
    }}
  />
);

EditListingPricingAndStockFormComponent.defaultProps = {
  fetchErrors: null,
  listingMinimumPriceSubUnits: 0,
  formId: 'EditListingPricingAndStockForm',
};

EditListingPricingAndStockFormComponent.propTypes = {
  formId: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  marketplaceCurrency: string.isRequired,
  listingMinimumPriceSubUnits: number,
  unitType: string.isRequired,
  listingType: shape({ stockType: string }).isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
};

export default compose(injectIntl)(EditListingPricingAndStockFormComponent);
